import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { BookOpen } from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { useTelegram } from '@/shared/lib/telegram'
import { FavoriteButton } from '@/features/favorites/ui'
import { useAddToCart } from '@/features/cart/api/use-cart'
import { useDrinkHistory } from '@/features/drink-history/api'
import { QuickAddSheet } from '@/widgets/quick-add-sheet'
import { ProductWeightSelectorSheet } from '@/widgets/product-weight-selector-sheet'
import { useCartNotification } from '@/widgets/cart-notification'
import { formatPrice, formatWeight, getWeightOptions } from '@/shared/lib/format'
import { calculateTeaPrice } from '@/shared/lib/pricing'
import { preloadOptimizedImage } from '@/shared/lib/image-optimization'
import { OptimizedImage } from '@/shared/ui/optimized-image'
import type { Tea } from '../model/types'

export interface TeaCardProps {
  /**
   * Чай для отображения
   */
  tea: Tea
  /**
   * Callback при клике на карточку
   */
  onClick?: () => void
}

/**
 * UI компонент: Карточка чая для каталога
 * 
 * Специальная карточка чая с кнопками:
 * - В дневник (записать чашку)
 * - В корзину (с выбором веса)
 * - В избранное (унифицированная кнопка)
 */
export function TeaCard({ tea, onClick }: TeaCardProps) {
  const navigate = useNavigate()
  const { hapticFeedback, initData } = useTelegram()
  const addToCart = useAddToCart()
  const cartNotification = useCartNotification()
  
  // Проверяем, есть ли чай в дневнике
  const { data: drinkHistoryData } = useDrinkHistory({ limit: 1000 })
  const teaInDiary = drinkHistoryData?.data?.some(entry => entry.teaId === tea.id) || false
  
  const [isDiarySheetOpen, setIsDiarySheetOpen] = useState(false)
  const [isWeightSheetOpen, setIsWeightSheetOpen] = useState(false)
  
  const mainImage = tea.images?.[0]?.url || '/placeholder-product.svg'
  const pricePerGram = tea.priceAmount || 0
  
  const handleCardClick = () => {
    if (onClick) {
      onClick()
    } else {
      navigate({
        to: '/product/$productId',
        params: { productId: tea.id },
      })
    }
  }
  
  const handleDiaryClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    hapticFeedback.impactOccurred('medium')
    setIsDiarySheetOpen(true)
  }
  
  const handleCartClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    hapticFeedback.impactOccurred('medium')
    setIsWeightSheetOpen(true)
  }
  
  // Добавление в корзину с выбранным весом
  const handleAddToCartWithWeight = (weight: number) => {
    addToCart.mutate({
      productType: 'tea',
      productId: tea.id,
      quantity: 1,
      weight,
    }, {
      onSuccess: (result) => {
        // Показываем премиальное уведомление
        cartNotification.show({
          productName: `${tea.name} · ${formatWeight(weight)}`,
          productImage: tea.images?.[0]?.url,
          cartItemId: result.data?.id,
        })
        hapticFeedback.notificationOccurred('success')
      },
      onError: () => {
        hapticFeedback.notificationOccurred('error')
      }
    })
  }

  const handleCardPreview = () => {
    preloadOptimizedImage(mainImage, { preset: 'hero' })
  }
  
  return (
    <>
      <motion.div
        whileTap={{ scale: 0.98 }}
        transition={{ type: "spring", stiffness: 400, damping: 17 }}
        onClick={handleCardClick}
        onPointerEnter={handleCardPreview}
        onTouchStart={handleCardPreview}
        className="bg-white dark:bg-dark-card rounded-[16px] overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.04)] dark:shadow-[0_4px_12px_rgba(0,0,0,0.3),0_2px_4px_rgba(0,0,0,0.2)] border border-gray-200 dark:border-gray-700 cursor-pointer hover:shadow-[0_4px_16px_rgba(0,0,0,0.12),0_2px_4px_rgba(0,0,0,0.08)] dark:hover:shadow-[0_8px_24px_rgba(0,0,0,0.4),0_4px_8px_rgba(0,0,0,0.3)] transition-all duration-300"
      >
        {/* Фото */}
        <div className="relative aspect-square bg-[#EDE5D6] dark:bg-[#231712] overflow-hidden rounded-[16px]">
          <OptimizedImage
            src={mainImage}
            alt={tea.name}
            preset="card"
            wrapperClassName="absolute inset-0"
            className="object-cover"
            loading="lazy"
            decoding="async"
          />
          {/* Градиент поверх фото для читаемости */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-transparent pointer-events-none" />
          
          {/* Бейдж "В дневнике" */}
          {initData && teaInDiary && (
            <div className="absolute top-2 left-2 px-2.5 py-1 bg-primary/90 dark:bg-primary-dark/90 backdrop-blur-sm rounded-[8px] text-white text-xs font-semibold flex items-center gap-1">
              <BookOpen className="w-3 h-3" />
              В дневнике
            </div>
          )}
          
          {/* Кнопка избранного - унифицированный компонент */}
          <div className="absolute top-2 right-2">
            <FavoriteButton
              productId={tea.id}
              variant="card"
              size="md"
              animated
            />
          </div>
          
          {/* Бейдж "Новинка" */}
          {tea.isNew && (
            <div className="absolute bottom-2 left-2 px-2 py-1 bg-primary/90 dark:bg-primary-dark/90 backdrop-blur-sm rounded-full text-xs font-medium text-white">
              Новинка
            </div>
          )}
        </div>
        
        {/* Контент */}
        <div className="p-3 space-y-2.5">
          {/* Название */}
          <div>
            <h3 className="text-sm font-semibold text-ink dark:text-white line-clamp-1 mb-1">
              {tea.name}
            </h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-1">
              {tea.categoryName || 'Чай'} · {tea.originCountry || tea.originRegion || '—'}
            </p>
          </div>
          
          {/* Цена */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-baseline gap-1.5">
              {tea.isCakeOnly ? (
                <>
                  <p className="text-base font-semibold text-primary dark:text-primary-light">
                    {formatPrice(calculateTeaPrice({ pricePerGram: tea.priceAmount || 0, weight: tea.cakeWeight ?? 357, quantity: 1, cakePriceAmount: tea.cakePriceAmount, cakeWeight: tea.cakeWeight }).unitPrice)}
                  </p>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    блин
                  </span>
                </>
              ) : tea.isOnSale && tea.salePriceAmount ? (
                <>
                  <p className="text-base font-semibold text-primary dark:text-primary-light">
                    от {formatPrice(tea.salePriceAmount * 10)}
                  </p>
                  <p className="text-xs text-gray-400 line-through">
                    {formatPrice((tea.priceAmount || 0) * 10)}
                  </p>
                </>
              ) : (
                <p className="text-base font-semibold text-primary dark:text-primary-light">
                  от {formatPrice((tea.priceAmount || 0) * 10)}
                </p>
              )}
              {!tea.isCakeOnly && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  за 10г
                </span>
              )}
            </div>
          </div>
          
          {/* Кнопки действий */}
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              onClick={handleDiaryClick}
              className={cn(
                'flex-1 flex items-center justify-center gap-1 py-1.5 px-2 rounded-lg',
                'bg-primary/10 dark:bg-primary-dark/10 text-primary dark:text-primary-light font-medium text-xs',
                'hover:bg-primary/20 dark:hover:bg-primary-dark/20',
                'active:bg-primary/30 dark:active:bg-primary-dark/30',
                'transition-colors touch-target'
              )}
            >
              <BookOpen className="h-3.5 w-3.5 flex-shrink-0" />
              <span className="hidden sm:inline">В дневник</span>
            </button>
            
            <button
              type="button"
              onClick={handleCartClick}
              disabled={!tea.isInStock}
              className={cn(
                'flex items-center justify-center w-10 h-10 rounded-full',
                'bg-primary dark:bg-primary-dark text-white',
                'hover:bg-primary-dark dark:hover:bg-primary-darker',
                'active:scale-95 transition-all',
                'touch-target disabled:opacity-50 disabled:cursor-not-allowed',
                'shadow-sm hover:shadow-md dark:shadow-md dark:hover:shadow-lg',
                'text-xl font-bold leading-none'
              )}
              aria-label="В корзину"
            >
              +
            </button>
          </div>
        </div>
      </motion.div>
      
      {/* Bottom Sheet: Быстрая запись в дневник */}
      <QuickAddSheet
        isOpen={isDiarySheetOpen}
        onClose={() => setIsDiarySheetOpen(false)}
        tea={tea}
        onSuccess={() => {
          hapticFeedback.notificationOccurred('success')
        }}
      />
      
      {/* Bottom Sheet: Выбор веса для корзины — унифицированный компонент */}
      <ProductWeightSelectorSheet
        isOpen={isWeightSheetOpen}
        onClose={() => setIsWeightSheetOpen(false)}
        weights={getWeightOptions(tea.isCakeAvailable ?? false, tea.isCakeOnly, tea.cakeWeight).map(weight => ({
          weight,
          price: calculateTeaPrice({ pricePerGram, weight, quantity: 1, cakePriceAmount: tea.cakePriceAmount, cakeWeight: tea.cakeWeight }).unitPrice,
          available: true,
        }))}
        cakeWeight={tea.cakeWeight}
        onSelect={handleAddToCartWithWeight}
        pricePerGram={pricePerGram}
      />
    </>
  )
}
